import * as Sentry from '@sentry/react';
import { useEffect, useState, type ReactElement } from 'react';

import { ConnectSamlSsoModal } from '@/components/saml-sso/ConnectSamlSsoModal';
import { getErrorMessage } from '@/hooks/api/errorHelpers';
import {
  useDisconnectOrgSSO,
  useOrgSSOStatus,
} from '@/hooks/api/useSSOSettings';
import { useToast } from '@/hooks/use-toast';
import type {
  GrantedIntegrationAccess,
  RenderableIntegrationAccess,
} from '@/hooks/useIntegrationAccess';
import { DisconnectSSODialog } from '@/pages/organization/components/DisconnectSSODialog';

import { SamlSsoIntegrationCard } from './SamlSsoIntegrationCard';

interface SamlSsoIntegrationProps {
  access: RenderableIntegrationAccess;
}

export function SamlSsoIntegration({
  access,
}: SamlSsoIntegrationProps): ReactElement {
  if (access.canRender) return <SamlSsoIntegrationInner access={access} />;
  return (
    <SamlSsoIntegrationCard
      status={{
        tenantId: '',
        projectId: '',
        spEntityId: '',
        spACSUrl: '',
        ssoId: '',
        isConfigured: false,
        domains: [],
      }}
      isLoading
    />
  );
}

SamlSsoIntegration.displayName = 'SamlSsoIntegration';

function SamlSsoIntegrationInner({
  access,
}: {
  access: GrantedIntegrationAccess;
}): ReactElement | null {
  const { organizationId, isLoading: isAccessLoading } = access;

  const ssoQuery = useOrgSSOStatus(organizationId);
  const disconnectMutation = useDisconnectOrgSSO(organizationId);
  const { toast } = useToast();

  const ssoError = ssoQuery.isError
    ? getErrorMessage(ssoQuery.error)
    : undefined;

  useEffect(() => {
    if (!ssoQuery.isError) return;
    Sentry.captureException(ssoQuery.error, {
      tags: { error_type: 'sso_status_fetch' },
      extra: { organizationId },
    });
  }, [ssoQuery.isError, ssoQuery.error, organizationId]);

  const [connectOpen, setConnectOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const openConnect = (): void => setConnectOpen(true);
  const openDisconnect = (): void => setDisconnectOpen(true);
  const closeDisconnect = (): void => setDisconnectOpen(false);

  const handleRequestDisconnect = (): void => {
    setConnectOpen(false);
    setDisconnectOpen(true);
  };

  const handleConfirmDisconnect = (): void => {
    disconnectMutation.mutate(undefined, {
      onSuccess: () => {
        setDisconnectOpen(false);
        toast({
          title: 'SSO disconnected',
          description: 'SSO configuration has been cleared successfully.',
        });
      },
      onError: error => {
        Sentry.captureException(error, {
          tags: { error_type: 'saml_disconnect' },
          extra: { organizationId },
        });
        toast({
          title: 'Disconnect failed',
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      },
    });
  };

  const isCardLoading = isAccessLoading || ssoQuery.isLoading;
  const status = ssoQuery.data ?? {
    tenantId: '',
    projectId: '',
    spEntityId: '',
    spACSUrl: '',
    ssoId: '',
    isConfigured: false,
    domains: [],
  };

  const cardCallbacks = ssoQuery.isError
    ? {}
    : {
        onConnect: openConnect,
        onManage: openConnect,
        onDisconnect: openDisconnect,
      };

  return (
    <>
      <SamlSsoIntegrationCard
        status={status}
        {...cardCallbacks}
        isLoading={isCardLoading}
        error={ssoError}
      />
      {connectOpen && (
        <ConnectSamlSsoModal
          key={organizationId}
          open={connectOpen}
          onOpenChange={setConnectOpen}
          organizationId={organizationId}
          onRequestDisconnect={handleRequestDisconnect}
          readOnly={!access.canWrite}
        />
      )}
      {disconnectOpen && (
        <DisconnectSSODialog
          isOpen={disconnectOpen}
          onClose={closeDisconnect}
          onConfirm={handleConfirmDisconnect}
          isLoading={disconnectMutation.isPending}
          readOnly={!access.canWrite}
        />
      )}
    </>
  );
}

SamlSsoIntegrationInner.displayName = 'SamlSsoIntegrationInner';
